import { useId } from 'react';

/**
 * Swipe Opinion brand logo — the single source of truth for the mark + wordmark
 * (2026-06-29 refresh). Do NOT inline the SVG elsewhere; import this component.
 *
 * Mark: TWO bold overlapping right-chevrons (a forward "swipe"), rounded
 * caps/joins. Three render modes (approved brand-sheet geometry):
 *   gradient — back = violet #7C6CFF, front = violet→indigo gradient (light bg).
 *   white    — back = white @85%, front = white (dark panels: login, admin).
 *   mono     — both chevrons one colour (navy/gray) for footers / print.
 *
 * Wordmark: "Swipe" (General Sans Semibold) + "Opinion" (General Sans Regular) in
 * navy. `font-brand` is General Sans (loaded via Fontshare in index.html), with
 * an Outfit fallback. Pass `wordmarkClassName` to recolor on dark surfaces.
 *
 * Variants:
 *   full — mark + wordmark (gradient mark, navy wordmark) — the default lockup.
 *   mark — mark only.
 *   mono — single-colour mark + wordmark (navy, or muted gray via monoColor).
 * Pass markVariant="white" to force the white mark on a dark panel while keeping
 * variant="full".
 */
export type LogoVariant = 'full' | 'mark' | 'mono';
export type MarkMode = 'gradient' | 'white' | 'mono';

interface Props {
  variant?: LogoVariant;
  /** Force a specific mark render mode. Defaults: variant="mono" → 'mono',
   *  otherwise 'gradient'. Set 'white' for dark panels. */
  markVariant?: MarkMode;
  /** Wrapper classes (e.g. layout). */
  className?: string;
  /** Sizes (and optionally tiles) the mark container, e.g. "w-7 h-7". */
  markClassName?: string;
  /** Overrides wordmark color/size — e.g. "text-white text-2xl" on dark. */
  wordmarkClassName?: string;
  /** mono only: 'navy' (#0F172A) or 'gray' (#6B7280). Default 'navy'. */
  monoColor?: 'navy' | 'gray';
  /** Accessible label for the whole lockup. */
  title?: string;
}

const MONO_HEX: Record<'navy' | 'gray', string> = {
  navy: '#0F172A',
  gray: '#6B7280',
};

// Approved double-chevron geometry (viewBox 0 0 100 100). Do not redesign.
const BACK_CHEVRON = 'M 24 26 L 48 50 L 24 74';
const FRONT_CHEVRON = 'M 42 26 L 66 50 L 42 74';

function cx(...parts: Array<string | false | undefined>): string {
  return parts.filter(Boolean).join(' ');
}

function ChevronMark({ mode, gradientId, monoStroke }: { mode: MarkMode; gradientId: string; monoStroke: string }) {
  const backStroke = mode === 'gradient' ? '#7C6CFF' : mode === 'white' ? '#ffffff' : monoStroke;
  const backOpacity = mode === 'white' ? 0.85 : 1;
  const frontStroke = mode === 'gradient' ? `url(#${gradientId})` : mode === 'white' ? '#ffffff' : monoStroke;
  return (
    <svg viewBox="0 0 100 100" fill="none" className="w-full h-full" aria-hidden="true">
      {mode === 'gradient' && (
        <defs>
          <linearGradient id={gradientId} x1="20" y1="20" x2="80" y2="80" gradientUnits="userSpaceOnUse">
            <stop offset="0" stopColor="#7C6CFF" />
            <stop offset="1" stopColor="#5B5CEB" />
          </linearGradient>
        </defs>
      )}
      <path d={BACK_CHEVRON} fill="none" stroke={backStroke} strokeOpacity={backOpacity} strokeWidth={16} strokeLinecap="round" strokeLinejoin="round" />
      <path d={FRONT_CHEVRON} fill="none" stroke={frontStroke} strokeWidth={16} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export default function SwipeOpinionLogo({
  variant = 'full',
  markVariant,
  className,
  markClassName = 'w-7 h-7',
  wordmarkClassName,
  monoColor = 'navy',
  title = 'Swipe Opinion',
}: Props) {
  // useId keeps each instance's gradient <linearGradient> id unique so multiple
  // logos on one page (nav + footer) don't collide on the same defs id.
  const gid = `so-logo-grad-${useId().replace(/:/g, '')}`;
  const isMono = variant === 'mono';
  // Mark mode: explicit markVariant wins; else mono variant → mono; else gradient.
  const mode: MarkMode = markVariant ?? (isMono ? 'mono' : 'gradient');
  const showWordmark = variant !== 'mark';

  // Wordmark default color: mono → its hex; full → navy. Overridable.
  const wordmarkColorClass = wordmarkClassName
    ? ''
    : isMono && monoColor === 'gray'
      ? 'text-[#6B7280]'
      : 'text-navy';

  return (
    <span className={cx('inline-flex items-center gap-2', className)} title={title}>
      <span className={cx('inline-flex items-center justify-center flex-shrink-0', markClassName)}>
        <ChevronMark mode={mode} gradientId={gid} monoStroke={MONO_HEX[monoColor]} />
      </span>
      {showWordmark && (
        <span className={cx('font-brand leading-none whitespace-nowrap', wordmarkColorClass, wordmarkClassName)}>
          <span className="font-semibold">Swipe</span> <span className="font-normal">Opinion</span>
        </span>
      )}
    </span>
  );
}
